import { useEffect, useState } from "react";

import { Loader, Sparkles } from "lucide-react";
import { type UseFormReturn } from "react-hook-form";

import useRouterQuery from "@/components/hooks/useRouterQuery";
import LoadingButton from "@/components/LoadingButton";
import TipTapEditor from "@/components/tiptap/Editor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api, handleApiRequest } from "@/lib/utils";

interface AiGeneratorProps {
  form: UseFormReturn<any>;
  fieldName: string;
  aiPrompt: Record<string, unknown>;
  isFreeform?: boolean;
}

export default function AiGenerator({ form, fieldName, aiPrompt, isFreeform }: AiGeneratorProps) {
  const websiteId = useRouterQuery("websiteId");
  const [additionalContext, setAdditionalContext] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const getAIResponse = async () => {
    await handleApiRequest({
      makeRequest: async () => {
        setValue("");
        setIsLoading(true);
        const aiPromptCopy = { ...aiPrompt };
        if (additionalContext.trim().length > 0) {
          aiPromptCopy.additionalContext = additionalContext;
        }

        const response = await api.post("/api/ai/content-suggestions", {
          aiPrompt: aiPromptCopy,
          websiteId: websiteId,
        });
        const text = (response.data || "").replace(/^"|"$/g, "");
        setValue(text);
        return response.data;
      },
      finallyCallback: () => {
        setIsLoading(false);
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <Tooltip>
        <div className="absolute top-8 right-2">
          <TooltipTrigger asChild>
            <button
              className="p-1 rounded-md bg-rebolt-100"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsOpen(true);
                e.stopPropagation();
              }}
            >
              <Sparkles className="h-4 w-4 text-rebolt-600" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Let us generate this for you
            <br /> based on best practices
          </TooltipContent>
        </div>
      </Tooltip>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>AI Generated</DialogTitle>
            <DialogDescription>We&apos;ll help you generate ideas using AI.</DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader className="h-8 w-8 text-ash-400 animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center space-x-2 gap-4">
                {value.trim().length > 0 && (
                  <div className="grid flex-1 gap-2 w-full">
                    <Label htmlFor="ai-content">AI Generated Content</Label>

                    {isFreeform ? (
                      <TipTapEditor
                        initialContent={value}
                        onUpdate={(html) => setValue(html)}
                        className="max-h-[350px] overflow-y-scroll"
                      />
                    ) : (
                      <Textarea
                        id="ai-content"
                        className="h-40"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                      />
                    )}
                  </div>
                )}
                <div className="flex flex-col gap-2 w-full">
                  <Label htmlFor="additional-context" className="flex gap-1 items-center">
                    Add Additional Context
                  </Label>
                  <Textarea
                    id="additional-context"
                    className="h-40"
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="Enter any additional context you want to be used while generating the content."
                  />
                </div>
              </div>
              <DialogFooter className="flex justify-between">
                <LoadingButton type="button" variant="outline" className="relative" onClick={getAIResponse}>
                  Generate Content
                </LoadingButton>

                <Button
                  onClick={() => {
                    form.setValue(fieldName, value, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                    setIsLoading(true);
                    setIsOpen(false);
                  }}
                  disabled={value.trim().length === 0}
                  type="button"
                >
                  Use this
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
